/**
 * What: this is a component that tracks promotions
 * Why: simplify promotion tracking
 * How: over all we use same data for impressions and interactions so why not use one component for it
 *      1. wrap the element we want to track with promotion component
 *          - promotion component should additionally wrap the element that captures interactions and handles them
 *      2. if the element is visible trigger tracking event
 *
 * End goal -> wrap the element that need to be tracked for promotion, provide props and thats that, rest is handled in tracking package
 *
 * Also same idea for product interactions
 */
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//tracker.types';
import {CMSTeaser} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/cms_teaser.types';
import {Outfit} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/outfit.types';
import {Story} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/story.types';
import {GetTopBannerResponse_TopBannerResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
  TrackingPromotionCreative,
  TrackingRecoElement,
  trackPromotionInteraction,
} from '@aboutyou/legacy-tracking-sdk';
import type {JSX} from 'react';
import React, {FC, PropsWithChildren} from 'react';
import {MarkOptional} from 'ts-essentials';
import {useOriginClick} from '../utils/getOriginClick';
import {getPromotionData} from '../utils/promotionDataGetter';
import {Brand} from '../utils/types';
import {useTrackingFactory} from '../utils/useTrackingFactory';
import {useTracking} from '../utils/utils';
import {waitUntilContentView} from '../utils/waitUntilContentView';
import {BaseTracker, BaseTrackerProps} from './BaseTracker';

export type Promotion =
  | PromotionAdvertisement
  | BannerPromotion
  | BasOspSubscriptionTilePromotion
  | BasSovendusTilePromotion
  | BrandPromotion
  | OutfitRecoPromotion
  | StoryRecoPromotion
  | TeaserRecoPromotion
  | TopBannerRecoPromotion
  | BasPromotionMonitoring
  | HighlightPromotion
  | CustomPagesPromotion
  | NewsCardPromotion
  | BasFlyoutTeaserPromotion
  | PromotionNotificationCenterCards
  | BrandOutfitTeaserRecoPromotion
  | BrandHeroElementPromotion
  | APIPromotion
  | OutfitSliderPromotion
  | GenericPromotion;

export type PromotionAdvertisementSource =
  | TrackingPromotionCreative.BasAdpBanner
  | TrackingPromotionCreative.BasBasketBanner
  | TrackingPromotionCreative.BasBrowseTopBanner
  | TrackingPromotionCreative.BasCategoryHeader
  | TrackingPromotionCreative.BasCategoryTeaser
  | TrackingPromotionCreative.BasLogoIntegration
  | TrackingPromotionCreative.BasSearch
  | TrackingPromotionCreative.BasWishlistBanner
  | TrackingPromotionCreative.Link
  | TrackingPromotionCreative.BasAddToBasketFlyOut
  | TrackingPromotionCreative.BasOspBanner;

export interface PromotionNotificationCenterCards {
  source: TrackingPromotionCreative.NotificationCenterCards;
  notificationCenterCards: {
    id: number | string;
    name: string;
    path: string;
    locationId: string;
    creative: string;
    destination: string;
    originClick: string;
  };
}

export interface PromotionAdvertisement {
  source: PromotionAdvertisementSource;
  advertisement: {
    id: number | string;
    name: string;
    path: string;
    locationId?: string;
    promotionId?: number | string;
    creativeName?: string;
    creativeSlot?: string;
  };
}

export interface BasFlyoutTeaserPromotion {
  source: TrackingPromotionCreative.BasFlyoutTeaser;
  advertisement: {
    id: number | string;
    name: string;
    path: string;
    promotionId?: number | string;
    creativeName?: string;
    locationId?: string;
  };
}

export interface TeaserRecoPromotion {
  source: TrackingPromotionCreative.Teaser | TrackingPromotionCreative.Story;
  teaser: Omit<CMSTeaser, 'image' | 'link' | 'tracker'> & {
    promotionId?: string | number;
    link: string;
  };
}

interface CampaignBannerPromotion {
  source: TrackingPromotionCreative.Banner;
  campaign: {
    name: string;
    id: number;
    path: string;
    linkedContent?: string;
    destinationId?: string;
  };
}

interface BrandOutfitTeaserRecoPromotion {
  source: TrackingPromotionCreative.Teaser;
  outfit: Outfit;
}

interface BrandHeroElementPromotion {
  source: TrackingPromotionCreative.Teaser;
  heroElement: {
    promotionId: string;
    promotionName: string;
    locationId?: string;
    destination?: string;
    creativeName: string;
    creativeSlot: string;
  };
}

interface APIPromotion {
  source: TrackingPromotionCreative.APIPromotion;
  apiPromotion: {
    promotionId: string;
    promotionName: string;
    creativeName: string;
    creativeSlot: string;
    locationId?: string;
    destination?: string;
    destinationUrl?: string;
  };
  isSponsored?: boolean;
}

interface TeaserBannerPromotion {
  source: TrackingPromotionCreative.Banner;
  teaser: CMSTeaser;
  sourceOverride: string;
}

interface HeroTileBannerPromotion {
  source: TrackingPromotionCreative.Banner;
  name: string;
  id?: string;
  ctaUrl?: string;
  external?: boolean;
  promotionTracker?: PromotionTracker;
}

export type BannerPromotion =
  | CampaignBannerPromotion
  | TeaserBannerPromotion
  | HeroTileBannerPromotion;

interface BasOspSubscriptionTilePromotion {
  source: TrackingPromotionCreative.BasOspSubscriptionTile;
  promotionId: string;
}

interface BasSovendusTilePromotion {
  source: TrackingPromotionCreative.Sovendus;
  promotionId: string;
}

export interface BrandPromotion {
  source: TrackingPromotionCreative.BrandReco | TrackingPromotionCreative.Brand;
  brand?: Brand;
  locationId?: string;
  promotionTracker?: PromotionTracker;
  isSponsored?: boolean;
}

export interface OutfitRecoPromotion {
  source: TrackingPromotionCreative.OutfitReco;
  outfit: Outfit;
}

export interface OutfitSliderPromotion {
  source:
    | TrackingPromotionCreative.outfitSliderADP
    | TrackingPromotionCreative.outfitSliderCategory
    | TrackingPromotionCreative.OutfitSliderBrand;
  promotionTracker: PromotionTracker;
}

export interface StoryRecoPromotion {
  source: TrackingPromotionCreative.StoryReco;
  story: Story & {promotionId?: string | number; locationId?: string};
}

export interface HighlightPromotion {
  source: TrackingPromotionCreative.Highlight;
  path?: string;
  external?: boolean;
  highlightId: number;
  promotionId: string;
  promotionName: string;
  trackHighlightIdAsPageId: boolean;
}

export interface TopBannerRecoPromotion {
  source: 'top_banner';
  banner: GetTopBannerResponse_TopBannerResponse;
  gender: 'male' | 'female' | 'kids';
}

export interface BasPromotionMonitoring {
  source: 'monitoring';
  originalSource:
    | TrackingPromotionCreative.BasCategoryHeader
    | TrackingPromotionCreative.BasCategoryTeaser
    | TrackingPromotionCreative.BasAdpBanner
    | TrackingPromotionCreative.BasBasketBanner
    | TrackingPromotionCreative.BasNavigationBanner
    | TrackingPromotionCreative.BasWishlistBanner
    | TrackingPromotionCreative.BasLogoIntegration
    | TrackingPromotionCreative.BasBrowseTopBanner
    | TrackingPromotionCreative.BasAddToBasketFlyOut
    | TrackingPromotionCreative.BasOspBanner;
  advertisement: {
    id: string;
    name: string;
  };
}

export interface RecoListTrackingData {
  sectionIndex: number;
  rowIndexInSection?: number;
  recoType?: string;
  headline?: string;
  elements?: TrackingRecoElement[];
  creativeName?: PromotionTracker['creativeName'];
  promotionId?: PromotionTracker['promotionId'];
  streamPosition?: number;
}

export interface ProductTrackingData
  extends MarkOptional<RecoListTrackingData, 'sectionIndex'> {
  sortingChannel?: string;
}

export type ContentBlocksSourceType = 'cta' | 'banner' | 'video';

export interface CustomPagesPromotion {
  source: TrackingPromotionCreative.CustomPage;
  contentBlockSource: ContentBlocksSourceType;
  id: string;
  name: string;
  locationId: string;
  path: {
    url: string;
    external: boolean;
  };
}

export interface NewsCardPromotion {
  source: TrackingPromotionCreative.Newscard;
  id: string;
  name: string;
  locationId: string;
  path: string;
}

export interface GenericPromotion {
  source: TrackingPromotionCreative.GenericPromotion;
  promotionTracker: PromotionTracker;
}

interface PromotionProps extends Omit<BaseTrackerProps, 'track'> {
  promotion: Promotion;
  recoList?: RecoListTrackingData;
  streamPosition: number;
  horizontalPosition?: number;
  threshold?: number;
  triggerOnce?: boolean;
  pageType?: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  ['data-testid']?: string;
  disabled?: boolean;
}

export function TrackPromotions({
  children,
  promotion,
  streamPosition,
  horizontalPosition,
  recoList,
  threshold = 0.5,
  triggerOnce,
  pageType,
  ...rest
}: PromotionProps) {
  const trackPromotion = usePromotionTracking({
    promotion,
    recoList,
    streamPosition,
    pageType,
    horizontalPosition,
  });

  return (
    <BaseTracker
      threshold={threshold}
      track={trackPromotion}
      triggerOnce={triggerOnce}
      {...rest}
    >
      {children}
    </BaseTracker>
  );
}

export const TrackPromotionStoryRecoNormalizer: FC<
  React.PropsWithChildren<
    {
      data: Record<string, any>;
      locationId: string;
      streamPosition: number;
    } & Omit<PromotionProps, 'promotion'>
  >
> = props => {
  const {
    data: {isPremium, image, headline, subline, path, id},
    locationId,
    ...rest
  } = props;

  return (
    <TrackPromotions
      promotion={{
        story: {
          promotionId: id,
          id,
          path,
          gender: 0,
          contentType: 0,
          headline: {default: headline},
          subline: {default: subline},
          locationId,
          isPremium,
          isSale: false,
          isSponsored: false,
          images: {landscape: image, portrait: image},
        },
        source: TrackingPromotionCreative.StoryReco,
      }}
      {...rest}
    >
      {props.children}
    </TrackPromotions>
  );
};

export const TrackPromotionTeaserRecoNormalizer: FC<
  PropsWithChildren<{
    data: Record<string, any>;
    streamPosition: number;
    recoList?: RecoListTrackingData;
    trackingKey?: number;
    Comp?: keyof JSX.IntrinsicElements;
  }>
> = ({streamPosition, data, recoList, trackingKey, children, Comp = 'div'}) => {
  const {testId, headline, subline, to, teaserName, linkTargetId, tracker} =
    data;

  return (
    <TrackPromotions
      promotion={{
        teaser: {
          promotionId: testId,
          headline,
          subline,
          link: to,
          promotionTracking: {
            id: testId,
            name: teaserName,
            linkTargetId,
            linkTargetType: '',
          },
        },
        source:
          tracker?.name === 'personalized_brand_stories_reco'
            ? TrackingPromotionCreative.Story
            : TrackingPromotionCreative.Teaser,
      }}
      streamPosition={streamPosition}
      recoList={recoList}
      trackingKey={trackingKey}
      Comp={Comp}
    >
      {children}
    </TrackPromotions>
  );
};

interface PromotionTrackingProps {
  promotion: Promotion;
  recoList?: RecoListTrackingData;
  streamPosition: number;
  pageType?: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  horizontalPosition?: number;
}

export function usePromotionTracking({
  promotion,
  recoList,
  streamPosition,
  pageType,
  horizontalPosition,
}: PromotionTrackingProps) {
  const {pageContextCallback} = useTracking();

  const track = useTrackingFactory(
    () =>
      getPromotionData({
        promotion,
        streamPosition,
        recoList,
        pageType: pageType || pageContextCallback().type,
        horizontalPosition,
      }),
    trackPromotionInteraction,
  );

  /**
   * View Promotion tracking should only be fired after contentView event is fired
   * to ensure that dataLayer has correct page type and page id
   */
  return (...args: Parameters<typeof track>) => {
    if (args[0] === 'impression') {
      const {waiter} = waitUntilContentView();

      waiter.then(() => {
        track(...args);
      });
    } else {
      track(...args);
    }
  };
}

export function usePromotionTrackingCallback() {
  const {pageContextCallback, TrackingClient} = useTracking();
  const trackOriginClick = useOriginClick();

  return ({
    promotion,
    recoList,
    streamPosition,
    pageType,
  }: PromotionTrackingProps) => {
    const pageContext = pageContextCallback();

    const trackingData = getPromotionData({
      promotion,
      streamPosition,
      recoList,
      pageType: pageType || pageContext.type,
    });

    return (type: 'impression' | 'click') => {
      if (pageContext && trackingData) {
        TrackingClient.track(
          trackPromotionInteraction({
            type,
            ...trackingData,
          }),
        );

        trackOriginClick(type, trackingData);
      }
    };
  };
}
